import { BrowserRouter, Route, Routes } from 'react-router-dom';
import { useT, LocaleProvider } from '@/i18n/runtime';
import { useCaptureStore } from '@/store/captureStore';
import { CaptureFlow } from './flow/CaptureFlow';
import { Done } from './flow/Done';
import { FrontDoor } from './flow/FrontDoor';
import { LiveLeg } from './flow/LiveLeg';
import { CentredNotice, Shell } from './flow/components/Shell';

/**
 * Routing (forked in shape from vCIP_droom_4.0/src/App.tsx):
 *   /v/:linkToken — the front door: exchange → confirm-request page → consent
 *   /capture      — the guided capture core (resumes via GET /state)
 *   /done         — the receipt
 *   /live         — the live officer-directed leg (SV-5-2): join when the case is waiting for a live call
 * The bare domain carries no request — the link IS the credential — so "/" tells the
 * representative to open their link rather than stranding them on a placeholder.
 */
export function App(): JSX.Element {
  const preferred = useCaptureStore((s) => s.summary?.locale ?? null);
  // SPEC_CAPTURE_UX_R2 §2.2: the customer's own case-scoped choice (be ≥ V178) outranks the tenant's preference.
  const chosen = useCaptureStore((s) => s.summary?.customerLocale ?? null);
  // VC-12: Arabic is offered only when the case's pack names it — absent on every India case. VC-12 r5: before the
  // exchange summary exists (the OTP code screen), the challenge's own `packLocales` stand in.
  const packLocales = useCaptureStore((s) => s.summary?.packLocales ?? s.frontDoorPackLocales ?? null);
  return (
    <LocaleProvider preferred={preferred} chosen={chosen} packLocales={packLocales}>
      <BrowserRouter>
        <Routes>
          <Route path="/" element={<LinkRequired />} />
          <Route path="/v/:linkToken" element={<FrontDoor />} />
          <Route path="/capture" element={<CaptureFlow />} />
          <Route path="/done" element={<Done />} />
          <Route path="/live" element={<LiveLeg />} />
          <Route path="*" element={<NotFound />} />
        </Routes>
      </BrowserRouter>
    </LocaleProvider>
  );
}

export function LinkRequired(): JSX.Element {
  const { t } = useT();
  return <Shell tenant={null}><CentredNotice title={t('sv.entry.linkRequired.title')} body={t('sv.entry.linkRequired.body')} /></Shell>;
}

export function NotFound(): JSX.Element {
  const { t } = useT();
  return <Shell tenant={null}><CentredNotice title={t('sv.entry.notFound.title')} body={t('sv.entry.notFound.body')} /></Shell>;
}
